import { openDB, deleteDB, DBSchema, IDBPDatabase } from 'idb';
import { withTimeout, isTimeoutError } from '../with-timeout';
import type { SyncFailureKind } from './retry-policy';

export interface SyncMutation {
  clientMutationId: string;
  entity: 'tickets' | 'jobs' | 'equipment' | 'customers';
  action: 'create' | 'update' | 'delete';
  id: string;
  data: Record<string, any>;
  version?: number;
  timestamp: string;
  /**
   * `failed` — retries exhausted. NOT a synonym for deleted: `cleanupQueue`
   * never removes it, and `SyncStatusBar` renders it in a red bar until a
   * person deals with it. Work that stopped being retried still has to be work
   * the user can see.
   *
   * No DB version bump: IndexedDB values are schemaless and `by-status` is an
   * index over the existing `status` key path, so a new value needs no upgrade.
   */
  status: 'pending' | 'applied' | 'conflict' | 'error' | 'failed';
  retryCount: number;
  /** Why the last attempt failed — drives whether there is a next one. */
  failureKind?: SyncFailureKind;
  /** Last error text, shown to the user in the failed-sync panel. */
  lastError?: string;
  /**
   * The server's per-row reasons for a rejected ticket save — `{index, field,
   * message}`, index into THIS mutation's `data.lineItems` — when /sync/push
   * sent them. What lets a refusal that arrives hours after the save land on
   * the line it is about (lib/ticket-discount `queuedDiscountRefusals`).
   * Absent from an older API, which degrades to the text above alone.
   */
  lastErrorDetails?: Array<{ index: number; field?: string; message: string }>;
}

export interface ConflictEntry {
  clientMutationId: string;
  entity: string;
  id: string;
  localData: Record<string, any>;
  serverRecord: Record<string, any>;
  reason: string;
  timestamp: string;
  dismissed: boolean;
}

export interface PendingUpload {
  id: string;
  entityType: string;
  entityId: string;
  fileName: string;
  contentType: string;
  blob: Blob;
  label?: string;
  localUrl: string;
  /** `failed` — see the note on `SyncMutation['status']`. */
  status: 'pending' | 'uploading' | 'done' | 'error' | 'failed';
  retryCount: number;
  failureKind?: SyncFailureKind;
  lastError?: string;
  createdAt: string;
}

interface FieldIQDB extends DBSchema {
  tickets: {
    key: string;
    value: any;
    indexes: {
      'by-job': string;
      'by-status': string;
      'by-updated': string;
    };
  };
  ticketLineItems: {
    key: string;
    value: any;
    indexes: {
      'by-ticket': string;
    };
  };
  jobs: {
    key: string;
    value: any;
    indexes: {
      'by-status': string;
      'by-updated': string;
    };
  };
  equipment: {
    key: string;
    value: any;
    indexes: {
      'by-status': string;
    };
  };
  customers: {
    key: string;
    value: any;
    indexes: {
      'by-name': string;
    };
  };
  syncQueue: {
    key: string;
    value: SyncMutation;
    indexes: {
      'by-timestamp': string;
      'by-status': string;
    };
  };
  syncMeta: {
    key: string;
    value: any;
  };
  conflictLog: {
    key: string;
    value: ConflictEntry;
    indexes: {
      'by-dismissed': string;
      'by-timestamp': string;
    };
  };
  pendingUploads: {
    key: string;
    value: PendingUpload;
    indexes: {
      'by-entity': string;
      'by-status': string;
    };
  };
}

export const DB_NAME = 'fieldiq-offline';
export const DB_VERSION = 3;

/**
 * How long `openDB` gets before the database is treated as unopenable.
 *
 * This is the same 4000ms budget the login purge uses, and deliberately so.
 * The purge's budget was sized against `openDB` *plus* nine `clear()` calls on
 * cheap eMMC in a low-end field Android; the open alone is the smaller half of
 * that, so a device that opens inside 4s is comfortably inside the population
 * this was tuned for. Giving the open its own, smaller budget would make a
 * slow-but-healthy device quarantine here that the login path would have let
 * through — a false positive costs that user their offline cache for the whole
 * session, which is a real loss for someone about to drive out of coverage.
 *
 * On the login path the two races therefore have the same budget and are, in
 * effect, one race: see the note on double-racing in `getDB`.
 */
export const DB_OPEN_TIMEOUT_MS = 4000;

/**
 * Budget for the recovery `deleteDatabase`. Always runs unawaited off any
 * user-facing path, so this only bounds how long we keep trying before giving
 * up and leaving the cache quarantined for the session.
 *
 * Owned here rather than in `tenant-cleanup` because both recovery paths — the
 * login purge's and `getDB`'s — need it, and `tenant-cleanup` imports this
 * module (the reverse import would be a cycle).
 */
export const CACHE_DELETE_TIMEOUT_MS = 3000;

/**
 * Cached as a *promise*, not as the resolved database.
 *
 * The previous `dbInstance = await openDB(...)` assigned only after the await,
 * so N concurrent callers each opened their own connection. Against a wedged
 * database that meant N never-settling requests piling up instead of one.
 */
let dbPromise: Promise<IDBPDatabase<FieldIQDB>> | null = null;

/**
 * Quarantine — set when we could not prove the offline cache was emptied.
 *
 * This is a tenant-isolation control, not an optimisation. `purgeTenantData`
 * wipes the previous tenant's cached tickets/jobs/customers before a new user
 * signs in. If that purge times out we let login continue, which on its own
 * would leave one tenant's records readable by the next user on the device.
 *
 * Quarantining closes that hole structurally: while it is set, `getDB()`
 * refuses to hand out a handle at all, so there is no path by which the
 * unpurged rows can be read, rendered, or pushed to the server. The cost is
 * that the session runs without an offline cache — which is the trade the
 * product wants (a working online session beats no session), and it is a
 * degradation the user is told about rather than a silent leak.
 */
let quarantined = false;
let quarantineReason: string | null = null;

/**
 * Module-scoped, so it dies with the JavaScript context. A quarantine lasts
 * exactly one app load: a page reload or a fresh Capacitor launch starts with
 * `quarantined === false` and re-attempts the open. That is what keeps the
 * amber banner from becoming permanent furniture — it is a statement about
 * this session, and the user is told restarting retries.
 */
const quarantineListeners = new Set<(quarantined: boolean, reason: string | null) => void>();

/**
 * Observe quarantine transitions.
 *
 * Exists so `sync-store` can mirror the flag into `cacheDegraded` (and so the
 * banner appears) without this module importing the store back — `sync-store`
 * already imports `getDB`, so the reverse import would be a cycle.
 */
export function onOfflineCacheQuarantineChange(
  listener: (quarantined: boolean, reason: string | null) => void
): () => void {
  quarantineListeners.add(listener);
  return () => { quarantineListeners.delete(listener); };
}

function notifyQuarantineChange(): void {
  for (const listener of Array.from(quarantineListeners)) {
    try {
      listener(quarantined, quarantineReason);
    } catch (err) {
      // A broken listener must not take down the recovery path that fired it.
      console.warn('[offline-db] Quarantine listener threw:', err);
    }
  }
}

export function isCacheQuarantined(): boolean {
  return quarantined;
}

export function getCacheQuarantineReason(): string | null {
  return quarantineReason;
}

/** Seal the offline cache for this session. Idempotent. */
export function quarantineOfflineCache(reason: string): void {
  const wasQuarantined = quarantined;
  quarantined = true;
  quarantineReason = reason;
  // Drop the cached handle/promise so nothing already in flight is reused.
  dbPromise = null;
  if (!wasQuarantined) notifyQuarantineChange();
}

/**
 * Lift the quarantine. Only legitimate once the data is provably gone —
 * i.e. the purge finally completed, or the database was deleted outright.
 */
export function releaseOfflineCacheQuarantine(): void {
  if (!quarantined) return;
  quarantined = false;
  quarantineReason = null;
  notifyQuarantineChange();
}

function cacheUnavailable(): Error {
  const err = new Error(
    `Offline cache unavailable: ${quarantineReason || 'quarantined'}`
  );
  err.name = 'OfflineCacheUnavailableError';
  return err;
}

/**
 * True for the rejection every guarded consumer is expected to handle: the
 * offline cache is sealed or could not be opened.
 *
 * Consumers branch on this rather than on any error, so a genuine bug — a bad
 * key path, a missing store — still surfaces instead of being silently
 * downgraded to "no cache".
 */
export function isCacheUnavailableError(err: unknown): boolean {
  return !!err && (err as any).name === 'OfflineCacheUnavailableError';
}

/**
 * Open the offline database, bounded in time.
 *
 * The guard lives *inside* `getDB` rather than at each call site because the
 * failure it defends against is not a rejection — it is `openDB` never
 * settling at all against a corrupted database, which walks straight past any
 * `try/catch` a caller could write. There is no correct way for a consumer to
 * opt out of that, and there were fourteen consumers to remember. Putting the
 * race here means every one of them inherits it, including any added later.
 *
 * DOUBLE-RACING ON THE LOGIN PATH — deliberate, and a no-op.
 * `purgeTenantData` already wraps `clearAllStores()` (which calls this) in its
 * own 4000ms race. Both budgets are 4000ms and the inner timer is registered
 * first, so on a wedged database the inner race settles first: `getDB` rejects,
 * `clearAllStores` rejects, and the outer race sees a rejection rather than
 * firing its own timer. Login's observable behaviour is unchanged — it still
 * gives up at ~4s, still quarantines, still proceeds to `/auth/login`. The
 * inner race cannot make login slower (it can only settle earlier than the
 * outer one) and cannot make it looser (both outcomes are quarantine). What it
 * adds is a bound for the other thirteen callers, which had none.
 */
export async function getDB(): Promise<IDBPDatabase<FieldIQDB>> {
  // Reject fast rather than hand back data we could not prove is this
  // tenant's. Callers already treat a rejected getDB() as "no cache".
  if (quarantined) throw cacheUnavailable();

  if (dbPromise) return dbPromise;

  // Held so the lifecycle callbacks below can close it. Assigned only if the
  // open ever lands, which against a wedged database it may not.
  let opened: IDBPDatabase<FieldIQDB> | null = null;

  const open = openDB<FieldIQDB>(DB_NAME, DB_VERSION, {
    blocked() {
      // Another tab holds an older version open, so our request sits pending
      // until it closes. This is one of the ways the open wedges; the race
      // below is what makes it survivable.
      console.warn('[offline-db] Open is blocked by another tab holding an older version.');
    },
    blocking() {
      // The mirror image: we are the stale connection standing in the way of
      // another tab's upgrade. Close, so that tab is not the one left wedged.
      // The next getDB() reopens at the new version.
      console.warn('[offline-db] Closing connection so another tab can upgrade.');
      dbPromise = null;
      try { opened?.close(); } catch { /* already closed */ }
    },
    terminated() {
      // The browser killed the connection under storage pressure. The cached
      // handle is dead — drop it so the next caller reopens rather than
      // operating on a corpse.
      console.warn('[offline-db] Connection was terminated by the browser.');
      dbPromise = null;
    },
    upgrade(db, oldVersion) {
      // v1 -> v2: wipe and recreate all stores
      if (oldVersion < 2) {
        const names = Array.from(db.objectStoreNames);
        for (const name of names) {
          db.deleteObjectStore(name);
        }

        const ticketStore = db.createObjectStore('tickets', { keyPath: 'id' });
        ticketStore.createIndex('by-job', 'job_id');
        ticketStore.createIndex('by-status', 'status');
        ticketStore.createIndex('by-updated', 'updated_at');

        const lineItemStore = db.createObjectStore('ticketLineItems', { keyPath: 'id' });
        lineItemStore.createIndex('by-ticket', 'ticket_id');

        const jobStore = db.createObjectStore('jobs', { keyPath: 'id' });
        jobStore.createIndex('by-status', 'status');
        jobStore.createIndex('by-updated', 'updated_at');

        const eqStore = db.createObjectStore('equipment', { keyPath: 'id' });
        eqStore.createIndex('by-status', 'status');

        const custStore = db.createObjectStore('customers', { keyPath: 'id' });
        custStore.createIndex('by-name', 'name');

        const syncStore = db.createObjectStore('syncQueue', { keyPath: 'clientMutationId' });
        syncStore.createIndex('by-timestamp', 'timestamp');
        syncStore.createIndex('by-status', 'status');

        db.createObjectStore('syncMeta');
      }

      // v2 -> v3: add conflictLog and pendingUploads (additive, no data loss)
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains('conflictLog')) {
          const conflictStore = db.createObjectStore('conflictLog', { keyPath: 'clientMutationId' });
          conflictStore.createIndex('by-dismissed', 'dismissed');
          conflictStore.createIndex('by-timestamp', 'timestamp');
        }
        if (!db.objectStoreNames.contains('pendingUploads')) {
          const uploadStore = db.createObjectStore('pendingUploads', { keyPath: 'id' });
          uploadStore.createIndex('by-entity', 'entityId');
          uploadStore.createIndex('by-status', 'status');
        }
      }
    },
  });

  open.then((db) => { opened = db; }, () => { /* handled by the race below */ });

  const guarded: Promise<IDBPDatabase<FieldIQDB>> = withTimeout(
    open,
    DB_OPEN_TIMEOUT_MS,
    `openDB(${DB_NAME})`
  ).catch((err) => {
    // Never cache a failure. A genuine rejection (quota, no IndexedDB at all)
    // may be transient, so the next caller gets to retry; a timeout is handled
    // by the quarantine below, which is what stops the retry loop.
    if (dbPromise === guarded) dbPromise = null;

    if (isTimeoutError(err)) {
      quarantineOfflineCache(
        `offline database did not open within ${DB_OPEN_TIMEOUT_MS}ms`
      );
      // Off the caller's path entirely — nothing user-facing waits on this.
      void recoverUnopenableDatabase(open);
      // Normalise, so every consumer branches on one error shape.
      throw cacheUnavailable();
    }

    throw err;
  });

  dbPromise = guarded;
  return guarded;
}

/** One recovery attempt per session — see `recoverUnopenableDatabase`. */
let recoveryAttempted = false;

/**
 * Try to get back to a usable cache after an open that never landed, without
 * blocking anybody.
 *
 * Deleting the database is the only lever left: the handle we could not obtain
 * is the same handle we would need to repair it. If the delete lands, the next
 * `getDB()` recreates the stores empty, so the quarantine can lift — the same
 * justification the login purge uses, and the reason lifting it here does not
 * weaken tenant isolation: an empty database cannot leak anyone's rows.
 *
 * Bounded to one attempt per session. If a freshly recreated database wedges
 * again the fault is below us, and a delete/recreate loop would just burn the
 * user's battery while the banner flickered.
 */
async function recoverUnopenableDatabase(open: Promise<IDBPDatabase<FieldIQDB>>): Promise<void> {
  if (recoveryAttempted) return;
  recoveryAttempted = true;

  // If the open does land eventually, close it. We are never going to use that
  // handle — the quarantine already ruled it out — and an open connection is
  // precisely what makes `deleteDatabase` block.
  open.then(
    (db) => { try { db.close(); } catch { /* already closed */ } },
    () => { /* already handled */ }
  );

  const deleted = await deleteOfflineDatabase(CACHE_DELETE_TIMEOUT_MS);
  if (deleted) {
    console.info('[offline-db] Unopenable database deleted; cache is clean again.');
    releaseOfflineCacheQuarantine();
    return;
  }

  console.warn(
    '[offline-db] Offline database could not be opened or deleted; it stays sealed for this session.'
  );
}

/**
 * Last-resort recovery: drop the database entirely so the next boot recreates
 * it empty.
 *
 * `deleteDB` hangs exactly like `openDB` does when connections are still held
 * open — the `blocked` event fires but `success` never does — so it is raced
 * too. Callers must not block anything user-facing on the result.
 */
let deleteInFlight: Promise<boolean> | null = null;

export async function deleteOfflineDatabase(timeoutMs: number): Promise<boolean> {
  // Single-flight. On the login path both recovery paths can fire — the
  // purge's and `getDB`'s — and two concurrent `deleteDatabase` calls against
  // one name is a way to block each other rather than a way to try harder.
  if (deleteInFlight) return deleteInFlight;

  dbPromise = null;
  deleteInFlight = withTimeout(deleteDB(DB_NAME), timeoutMs, `deleteDB(${DB_NAME})`)
    .then(() => true, () => false)
    .then((ok) => {
      deleteInFlight = null;
      return ok;
    });

  return deleteInFlight;
}

/** Test seam: reset module state between cases. */
export function __resetOfflineDbStateForTests(): void {
  dbPromise = null;
  quarantined = false;
  quarantineReason = null;
  recoveryAttempted = false;
  deleteInFlight = null;
  // Listeners are intentionally kept: `sync-store` registers one at module
  // load, and clearing it here would silently unwire the banner for every
  // later test in the file.
}

export type EntityStore = 'tickets' | 'ticketLineItems' | 'jobs' | 'equipment' | 'customers';

/**
 * Wipe all object stores in the offline DB.
 * Used on logout / tenant switch to prevent cross-tenant data leakage.
 */
export async function clearAllStores(): Promise<void> {
  const db = await getDB();
  await Promise.all([
    db.clear('tickets'),
    db.clear('ticketLineItems'),
    db.clear('jobs'),
    db.clear('equipment'),
    db.clear('customers'),
    db.clear('syncQueue'),
    db.clear('syncMeta'),
    db.clear('conflictLog'),
    db.clear('pendingUploads'),
  ]);
}
